import { Complete, CompleteInput } from "@lyfegen-platform/react-common";
import { CircularProgress, Divider, Omit, Typography } from "@material-ui/core";
import { AutocompleteChangeReason, AutocompleteProps } from "@material-ui/lab/Autocomplete";
import { useField, UseFieldProps } from "formik";
import { Fragment, memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NON_OPTION_VALUE } from "../models/common/options";
import { castToArray } from "../utils/agreement-utils";
import { isEmptyValue } from "../utils/is-empty-value";
import useStyles from "./component-styles";
import NewCompleteInput from "./NewCompleteInput";
import { PaperComponent, paperStyle } from "./OutlinedPopper";

interface Props {
	id?: string;
	title: string;
	optionLabel?: string;
	required?: boolean;
	loading?: boolean;
	property?: string;
	selectPropertyValue?: boolean;
	onClear?: () => void;
	isChildInFormik?: boolean;
	savedOnBlur?: true;
	shouldAutofill?: boolean;
}

const defaultProperty = "id";

function selectedValue<T>(actualProperty: keyof T, v: T | T[]) {
	if (isEmptyValue(v)) {
		return null;
	}

	return Array.isArray(v) ? v.map((v) => v?.[actualProperty]) : v?.[actualProperty];
}

function LAComplete<
	T,
	Multiple extends boolean | undefined = undefined,
	DisableClearable extends boolean | undefined = undefined,
	FreeSolo extends boolean | undefined = undefined
>(props: Props & Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, "renderInput"> & UseFieldProps<any>) {
	const { t } = useTranslation();
	const { inputLabelRoot, control } = useStyles({});
	const [{ value, ...fieldProps }, input, helpers] = useField(props);
	const { property, selectPropertyValue, onClear, ...forwardedProps } = props;
	const actualProperty = (property || defaultProperty) as keyof T;
	const actualOptionLabel = props.optionLabel || "label";
	const isError = input.touched && !!input.error;
	const [isCleared, setIsCleared] = useState(false);
	const { paper } = paperStyle();

	const matchValues = selectPropertyValue
		? (option: T, value: T | T[keyof T]) =>
				props.getOptionSelected ? props.getOptionSelected(option, value as any) : selectedValue(actualProperty, option) === value
		: (option: T, value: T | T[keyof T]) => selectedValue(actualProperty, option) === selectedValue(actualProperty, value as T);

	const originalValue = useMemo(() => {
		if (value === "") {
			return null;
		}

		if (isEmptyValue(value) || !forwardedProps.options) {
			return value;
		}

		return forwardedProps.multiple
			? (value as T[]).map((v) => forwardedProps.options.find((o) => matchValues(o, v))).filter(Boolean)
			: forwardedProps.options.find((o) => matchValues(o, value)) || null;
	}, [value, forwardedProps.options, forwardedProps.multiple, selectPropertyValue, selectedValue]);

	useEffect(() => {
		const isSingleSelectableOption =
			props.options.length === 1 ||
			(isEmptyValue(value) &&
				props.options.length > 1 &&
				(props.options[0] as any).group &&
				(props.options[0] as any).group === t("filtered-options") &&
				props.options.filter((option) => (option as any).group === (props.options[0] as any).group).length === 1);
		if (isSingleSelectableOption && (props.required || props.shouldAutofill) && !!props.name && !isCleared) {
			const valueToSet = selectPropertyValue ? selectedValue(actualProperty, props.options[0]) : props.options[0];

			if (
				!isEmptyValue(valueToSet) &&
				!matchValues(props.options[0], props.multiple ? value[0] : value) &&
				(valueToSet as any)?.value !== NON_OPTION_VALUE
			) {
				helpers.setValue(props.multiple ? castToArray<any>(valueToSet) : valueToSet);
			}
		}

		if (!isEmptyValue(value) && isEmptyValue(originalValue) && !props.isChildInFormik) {
			helpers.setValue("");
		}
	}, [props.options, originalValue, helpers, props.name, isCleared, props.shouldAutofill]);

	const optionKeys = props.options.map((o) => selectedValue(actualProperty, o)).join("|");
	useEffect(() => {
		setIsCleared(false);
	}, [optionKeys, setIsCleared]);

	function handleSelectValue(value: T, reason: AutocompleteChangeReason) {
		const checkedValue = reason === "blur" ? matchValueToOption(value, forwardedProps.options) : value;
		const selectedOption =
			props.freeSolo && props.property && typeof checkedValue === "string"
				? props.options.find((option) => (props.getOptionLabel?.(option) || (option as any)[actualOptionLabel]) === checkedValue) ||
				  checkedValue
				: checkedValue;
		helpers.setValue(
			selectPropertyValue && typeof checkedValue !== "string" ? selectedValue(actualProperty, selectedOption as any) : selectedOption
		);
		if (reason === "clear") {
			onClear?.();
			setIsCleared(true);
		}
	}

	function handleInputChange(value: string) {
		if (props.freeSolo) {
			helpers.setValue(value);
		}
	}

	function matchValueToOption(value: any, options: T[]) {
		if (typeof options[0] === "object") {
			return value;
		}

		return options.filter((option) => (option as unknown as number) === +value)[0] || null;
	}

	function getOptionLabel(option: any) {
		return props.getOptionLabel ? props.getOptionLabel(option) : option?.[actualOptionLabel];
	}

	if (props.type !== "date") {
		return (
			<>
				<Complete
					id={props.id || fieldProps.name}
					autoSelect
					{...fieldProps}
					{...forwardedProps}
					value={props.isChildInFormik ? value : originalValue}
					onChange={(_, v, reason) => handleSelectValue(v as any, reason)}
					onBlur={(e) => handleInputChange((e.target as any).value)}
					optionLabel={actualOptionLabel}
					noOptionsText={t("no_values_found")}
					PaperComponent={!props.PaperComponent ? PaperComponent : props.PaperComponent}
					classes={{ paper }}
					className={control}
					clearOnBlur={props.freeSolo !== undefined && !props.freeSolo}
					style={{ backgroundColor: "white" }}
					renderInput={(params) => {
						return (
							<NewCompleteInput
								handleSelectValue={handleSelectValue}
								savedOnBlur={props.savedOnBlur}
								disabled={props.disabled}
								error={isError ? input.error : undefined}
								params={{
									...params,
									InputProps: {
										...params.InputProps,
										endAdornment: (
											<Fragment>
												{props.loading ? <CircularProgress color="primary" size={20} /> : null}
												{params.InputProps.endAdornment}
											</Fragment>
										),
									},
								}}
								label={props.title}
								required={props.required}
							/>
						);
					}}
					renderOption={(option: any) =>
						option.appendix ? (
							<div>
								<Typography>{getOptionLabel(option)}</Typography>
								<Divider />
								<Typography variant="caption">{t(option.appendix)}</Typography>
							</div>
						) : (
							<>{getOptionLabel(option)}</>
						)
					}
				/>
			</>
		);
	}

	return (
		<>
			<Complete
				id={props.id || fieldProps.name}
				{...fieldProps}
				{...forwardedProps}
				value={originalValue}
				onChange={(_, v, reason) => handleSelectValue(v as any, reason)}
				optionLabel={props.optionLabel || "label"}
				noOptionsText={t("no_values_found")}
				renderInput={(params) => {
					return (
						<CompleteInput
							error={isError ? input.error : undefined}
							params={{
								...params,
								InputLabelProps: {
									shrink: true,
									classes: { root: inputLabelRoot },
								},
							}}
							label={props.required && !originalValue ? props.title + "*" : props.title}
							required={props.required && !originalValue}
						/>
					);
				}}
			/>
		</>
	);
}

export default memo(LAComplete);
